import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, deleteDoc, doc, writeBatch } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordTaskResults = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [wordTaskResults, setWordTaskResults] = useState([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)

  useEffect(() => {
    if (user) {
      loadTeacherWordTaskResults()
    }
  }, [user])

  const loadTeacherWordTaskResults = async () => {
    try {
      setLoading(true)
      const wordTasksRef = collection(db, 'wordTasks')
      const q = query(wordTasksRef, where('teacherId', '==', user.uid))
      const querySnapshot = await getDocs(q)
      
      const tempResults = []
      
      for (const wordTaskDoc of querySnapshot.docs) {
        const wordTaskData = wordTaskDoc.data()
        
        const result = {
          wordTaskId: wordTaskDoc.id,
          wordTaskTitle: wordTaskData.title || '',
          wordTaskDescription: wordTaskData.description || '',
          createdDate: wordTaskData.createdDate?.toDate() || new Date(),
          studentIds: wordTaskData.studentIds || [],
          studentNames: wordTaskData.studentNames || [],
          taskMode: wordTaskData.taskMode || 'study',
          className: wordTaskData.className || '',
          words: wordTaskData.words || [],
          studentResults: [],
          uniqueCompletedStudents: 0
        }
        
        tempResults.push(result)
      }
      
      // Kelime görevlerini createdDate'e göre sırala (en yeni en üstte)
      tempResults.sort((task1, task2) => {
        return new Date(task2.createdDate) - new Date(task1.createdDate)
      })
      
      // Her kelime görevi için öğrenci sonuçlarını yükle
      for (const result of tempResults) {
        await loadStudentResultsForWordTask(result)
      }
      
      setWordTaskResults(tempResults)
    } catch (error) {
      console.error('Error loading teacher word tasks:', error)
      toast.error('Kelime görevleri yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadStudentResultsForWordTask = async (wordTaskResult) => {
    try {
      const resultsRef = collection(db, 'wordTaskResults')
      const q = query(
        resultsRef,
        where('wordTaskId', '==', wordTaskResult.wordTaskId),
        where('teacherId', '==', user.uid)
      )
      const querySnapshot = await getDocs(q)
      
      // Her öğrenci için sadece en son sonucu al (tekrarları önle)
      const studentResultsMap = new Map()
      
      for (const resultDoc of querySnapshot.docs) {
        const resultData = resultDoc.data()
        const studentId = resultData.studentId
        const score = resultData.score || 0
        const totalQuestions = resultData.totalQuestions || 0
        const completionDate = resultData.completionDate?.toDate() || new Date()
        const taskMode = resultData.taskMode || 'study'
        
        // Eğer bu öğrenci için daha önce sonuç varsa, sadece en son olanı al
        if (studentResultsMap.has(studentId)) {
          const existingResult = studentResultsMap.get(studentId)
          if (completionDate > existingResult.completionDate) {
            // Yeni sonuç daha güncel, güncelle
            existingResult.score = score
            existingResult.totalQuestions = totalQuestions
            existingResult.completionDate = completionDate
            existingResult.taskMode = taskMode
          }
        } else {
          // İlk kez bu öğrenci için sonuç
          const studentResult = {
            studentId: studentId,
            score: score,
            totalQuestions: totalQuestions,
            completionDate: completionDate,
            taskMode: taskMode,
            studentName: ''
          }
          studentResultsMap.set(studentId, studentResult)
        }
      }
      
      // Öğrenci isimlerini al
      for (const [studentId, studentResult] of studentResultsMap) {
        try {
          const userDoc = await getDocs(query(
            collection(db, 'users'),
            where('__name__', '==', studentId)
          ))
          if (!userDoc.empty) {
            studentResult.studentName = userDoc.docs[0].data().username || 'Bilinmeyen Öğrenci'
          }
        } catch (error) {
          console.error('Error getting student name:', error)
          studentResult.studentName = 'Bilinmeyen Öğrenci'
        }
      }
      
      wordTaskResult.studentResults = Array.from(studentResultsMap.values())
      wordTaskResult.uniqueCompletedStudents = studentResultsMap.size
      
    } catch (error) {
      console.error('Error loading student results for word task:', error)
    }
  }

  const handleTaskClick = (task) => {
    // Detay sayfasına git
    onBack({
      page: 'word-task-detail',
      wordTaskId: task.wordTaskId,
      wordTaskTitle: task.wordTaskTitle
    })
  }

  const handleDeleteClick = (task) => {
    setSelectedTask(task)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedTask) return
    
    try {
      // Önce kelime görevi sonuçlarını sil
      const resultsRef = collection(db, 'wordTaskResults')
      const resultsQuery = query(
        resultsRef,
        where('wordTaskId', '==', selectedTask.wordTaskId)
      )
      const resultsSnapshot = await getDocs(resultsQuery)
      
      const batch = writeBatch(db)
      
      // Sonuçları sil
      resultsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
      })
      
      // Kelime görevini sil
      const wordTaskRef = doc(db, 'wordTasks', selectedTask.wordTaskId)
      batch.delete(wordTaskRef)
      
      await batch.commit()
      
      toast.success('Kelime görevi başarıyla silindi')
      setShowDeleteDialog(false)
      setSelectedTask(null)
      loadTeacherWordTaskResults() // Listeyi yenile
    } catch (error) {
      console.error('Error deleting word task:', error)
      toast.error('Kelime görevi silinirken hata oluştu')
    }
  }

  const getModeText = (mode) => {
    switch (mode) {
      case 'matching':
        return '🎯 Eşleştirme Oyunu'
      case 'meaning':
        return '❓ Anlam Sorma'
      default:
        return '📚 Çalışma Modu'
    }
  }

  const getModeColor = (mode) => {
    switch (mode) {
      case 'matching':
        return 'warning'
      case 'meaning':
        return 'info'
      default:
        return 'primary'
    }
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const calculateCompletionRate = (task) => {
    const totalStudents = task.studentIds?.length || 0
    const uniqueCompletedStudents = task.uniqueCompletedStudents || 0
    
    if (totalStudents === 0) return 0
    return (uniqueCompletedStudents / totalStudents) * 100
  }

  const calculateAverageScore = (task) => {
    const completedStudents = task.studentResults?.length || 0
    
    if (completedStudents === 0) return 0
    
    let totalScore = 0
    let totalQuestions = 0
    
    task.studentResults.forEach(studentResult => {
      totalScore += studentResult.score || 0
      totalQuestions += studentResult.totalQuestions || 0
    })
    
    if (totalQuestions === 0) return 0
    return (totalScore / totalQuestions) * 100
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Kelime Görevleri Sonuçları
            </Typography>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kelime Görevleri Sonuçları
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {wordTaskResults.length === 0 ? (
          <Alert severity="info" sx={{ textAlign: 'center' }}>
            Henüz kelime görevi oluşturulmamış
          </Alert>
        ) : (
          <List>
            {wordTaskResults.map((task, index) => (
              <Card key={task.wordTaskId} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Header with title and delete button */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" fontWeight="bold" color="primary" sx={{ flexGrow: 1 }}>
                      {task.wordTaskTitle}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(task)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  {/* Description */}
                  {task.wordTaskDescription && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {task.wordTaskDescription}
                    </Typography>
                  )}

                  {/* Mode and Class */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Chip
                      label={getModeText(task.taskMode)}
                      color={getModeColor(task.taskMode)}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      •
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {task.className || '-'}
                    </Typography>
                  </Box>

                  {/* Date */}
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {formatDate(task.createdDate)}
                  </Typography>

                  {/* Statistics */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      {task.studentResults.length}/{task.studentIds.length} öğrenci
                    </Typography>
                    
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      %{calculateCompletionRate(task).toFixed(1)} tamamlandı
                    </Typography>
                    
                    <Typography variant="body2" color="warning.main" fontWeight="bold">
                      Ortalama: %{calculateAverageScore(task).toFixed(1)}
                    </Typography>
                  </Box>

                  {/* Action Button */}
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleTaskClick(task)}
                    >
                      Detayları Görüntüle
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kelime Görevini Sil</DialogTitle>
        <DialogContent>
          <Typography>
            "{selectedTask?.wordTaskTitle}" kelime görevini silmek istediğinizden emin misiniz? 
            Bu işlem geri alınamaz ve tüm görev sonuçları da silinecektir.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            İptal
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default WordTaskResults
